import { yupResolver } from '@hookform/resolvers/yup';
import type { FieldValues, Resolver } from 'react-hook-form';
import type { AnyObjectSchema, Lazy } from 'yup';

/**
 * `useForm<TFieldValues>({ resolver })` wants a `Resolver<TFieldValues>`, but since
 * @hookform/resolvers v3 `yupResolver(schema)` returns `Resolver<yup.InferType<schema>>`
 * instead - the field-value type is derived from the schema.
 *
 * Those two types are never mutually assignable here, for two reasons that are properties
 * of the forms themselves rather than mistakes to be fixed:
 *   - our schemas validate a *subset* of each form's fields (a form with 20 fields often
 *     has rules for 5 of them), so the inferred type is missing the rest;
 *   - yup infers anything that is not `.required()` as optional, so even fully covered
 *     forms infer as `{ a?: X }` against a `{ a: X }` form type.
 *
 * yup's type system cannot express "this schema validates part of that form", so the
 * field-value type has to be re-asserted somewhere. Doing it here keeps it to one
 * explained place instead of ~40 casts at the call sites, and keeps the useful part of the
 * typing - the `TFieldValues` the component actually programs against - intact.
 */
export const yupFormResolver = <TFieldValues extends FieldValues>(
  schema: AnyObjectSchema | Lazy<any>
): Resolver<TFieldValues> => yupResolver(schema as AnyObjectSchema) as unknown as Resolver<TFieldValues>;
